export interface DashboardAlert {
  id: string;
  message: string;
  severity: 'critical' | 'warning' | 'info';
  time: string;
}

interface AlertsPanelProps {
  alerts: DashboardAlert[];
}

const severityConfig: Record<string, { icon: string; color: string; bg: string }> = {
  critical: { icon: 'ri-error-warning-fill', color: 'text-accent-500', bg: 'bg-accent-50' },
  warning: { icon: 'ri-alert-fill', color: 'text-accent-400', bg: 'bg-accent-50/50' },
  info: { icon: 'ri-information-fill', color: 'text-secondary-500', bg: 'bg-secondary-50' },
};

export default function AlertsPanel({ alerts }: AlertsPanelProps) {
  const criticalCount = alerts.filter((a) => a.severity === 'critical').length;

  return (
    <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-semibold text-foreground-800">Alerts</h4>
          {criticalCount > 0 && (
            <span className="text-xs px-1.5 py-0.5 rounded-full bg-accent-100 text-accent-600 font-medium">
              {criticalCount} critical
            </span>
          )}
        </div>
      </div>
      {alerts.length === 0 ? (
        <div className="py-10 flex flex-col items-center gap-2 text-center">
          <div className="w-9 h-9 rounded-full bg-secondary-50 flex items-center justify-center">
            <i className="ri-checkbox-circle-fill text-secondary-500 text-lg"></i>
          </div>
          <p className="text-sm text-foreground-400">All clear — no alerts right now</p>
        </div>
      ) : (
        <div className="space-y-1">
          {alerts.map((alert) => {
            const sev = severityConfig[alert.severity] || severityConfig.info;
            return (
              <div
                key={alert.id}
                className="flex items-start gap-3 py-2.5 px-2 rounded-md hover:bg-background-100 transition-colors cursor-pointer"
              >
                <div className={`w-7 h-7 rounded-full ${sev.bg} flex items-center justify-center shrink-0 mt-0.5`}>
                  <i className={`${sev.icon} ${sev.color} text-sm`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground-700">{alert.message}</p>
                  <span className="text-xs text-foreground-400 mt-0.5 block">{alert.time}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
